//lib/api.ts
import { fetchWithAuth } from './fetchWithAuth';
const API_BASE = process.env.NEXT_PUBLIC_API_BASE!;

async function parseApiError(res: Response, fallback: string): Promise<Error> {
    try {
        const data = await res.json();
        const message = Array.isArray(data?.message)
            ? data.message.join(', ')
            : data?.message ?? fallback;
        return new Error(message);
    } catch {
        try {
            const text = await res.text();
            return new Error(text || fallback);
        } catch {
            return new Error(fallback);
        }
    }
}

export async function fetchCertificates(
    page = 1,
    limit = 10,
    search?: string,
    options?: { scope?: 'me' | 'all' },
) {
    const params = new URLSearchParams({
        page: `${page}`,
        limit: `${limit}`,
    });
    if (search) params.append('search', search);

    const scope = options?.scope ?? 'me';
    const endpoint = scope === 'all' ? 'certificates/all' : 'certificates/me';
    const res = await fetchWithAuth(`${API_BASE}/${endpoint}?${params.toString()}`);

    if (!res.ok) throw new Error('Failed to fetch certificates');
    return res.json(); // { data, total, page, limit, totalPages }
}

export async function exportMyCertificatesCsv(search?: string) {
    const params = new URLSearchParams();
    if (search) params.append('search', search);

    const url = `${API_BASE}/certificates/me/export${params.toString() ? `?${params.toString()}` : ''}`;
    const res = await fetchWithAuth(url, {
        method: 'GET',
        headers: { Accept: 'text/csv' },
    });

    if (!res.ok) {
        throw new Error(`Failed to export certificates: ${res.statusText}`);
    }

    const blob = await res.blob();
    const disposition = res.headers.get('content-disposition') ?? '';
    const match = disposition.match(/filename="([^"]+)"/i);
    const filename = match?.[1] ?? 'sigalmark_certificates.csv';

    return { blob, filename };
}

export async function sendCertificateCompanyEmail(params: {
    certificateId: number;
    compositePng: Blob;
    resend?: boolean;
}) {
    const formData = new FormData();
    formData.append(
        'compositePng',
        params.compositePng,
        `certificate-${params.certificateId}.png`,
    );

    const resendParam = params.resend ? '?resend=true' : '';
    const res = await fetchWithAuth(`${API_BASE}/certificates/${params.certificateId}/email${resendParam}`, {
        method: 'POST',
        body: formData,
    });

    if (!res.ok) {
        const raw = await res.text().catch(() => '');
        throw new Error(raw || `Failed to send email: ${res.statusText}`);
    }

    return res.json();
}

export const issueCertificate = async (data: {
    name: string;
    companyEmail?: string;
    competency: string;
    expiry: string;
    imageURI?: string;
    contract?: string;
    networkId?: number;
}) => {

    const res = await fetchWithAuth(`${API_BASE}/certificates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        throw await parseApiError(res, `Failed to issue certificate: ${res.statusText}`);
    }

    return res.json();
};

export const issueCertificatesBatch = async (
    data: Array<{
        name: string;
        companyEmail?: string;
        competency: string;
        expiry: string;
        imageURI?: string;
        contract?: string;
        networkId?: number;
    }>,
) => {
    const res = await fetchWithAuth(`${API_BASE}/certificates/batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });

    if (!res.ok) {
        throw await parseApiError(res, `Failed to issue certificates: ${res.statusText}`);
    }

    return res.json();
};


export const revokeCertificate = async (id: number) => {
    const res = await fetchWithAuth(`${API_BASE}/certificates/${id}/revoke`, {
        method: 'PATCH',
    });
    if (!res.ok) {
        throw await parseApiError(res, `Failed to revoke certificate: ${res.statusText}`);
    }
    return res.json().catch(() => ({}));
};

export const extendCertificate = async (id: number, expiry?: string) => {
    const res = await fetchWithAuth(`${API_BASE}/certificates/${id}/extend`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(expiry ? { expiry } : {}),
    });
    if (!res.ok) {
        throw await parseApiError(res, `Failed to extend certificate: ${res.statusText}`);
    }
    return res.json().catch(() => ({}));
};

export const fetchCertificateByContract = async (
    contract: string,
    tokenId: number,
    networkId: number,
    tenantSlug?: string,
) => {
    const url = new URL(
        `${process.env.NEXT_PUBLIC_API_BASE}/certificates/verify/${contract}/${tokenId}/${networkId}`,
    );
    if (tenantSlug) {
        url.searchParams.set('tenantSlug', tenantSlug);
    }

    return fetch(url.toString())
        .then(async res => {
            const raw = await res.text();
            if (!res.ok || !raw) throw new Error('Certificate not found');
            return JSON.parse(raw);
        });
};

export type CertificateVerificationStats = {
    certificateId: number;
    viewsTotal: number;
    lastViewedAt: string | null;
    issuedByTenant: { id: number; slug: string; name: string } | null;
    issuedForCompany: { name: string; email: string | null };
};

export async function fetchCertificateVerificationStats(
    certificateId: number,
): Promise<CertificateVerificationStats> {
    const res = await fetchWithAuth(`${API_BASE}/certificates/${certificateId}/verification-stats`);
    if (!res.ok) {
        throw await parseApiError(res, 'Failed to load verification stats');
    }
    return res.json();
}

export type CertificateVerificationStatsRow = {
    id: number;
    name: string;
    companyEmail: string | null;
    competency: string;
    issued: string;
    expiry: string;
    status: string;
    contract: string;
    tokenId: number;
    networkId: number;
    imageURI?: string | null;
    explorerUrl?: string | null;
    shortUrl?: string | null;
    emailSentAt?: string | null;
    tenant: {
        id: number;
        slug: string;
        name: string;
        logoUrl?: string | null;
        tintElements?: string | null;
        tintText?: string | null;
        tintBackground?: string | null;
    } | null;
    viewsTotal: number;
    lastViewedAt: string | null;
};

export async function fetchCertificateVerificationStatsList(params?: {
    tenantSlug?: string;
    search?: string;
    from?: string;
    to?: string;
    limit?: number;
}): Promise<{ data: CertificateVerificationStatsRow[] }> {
    const url = new URL(`${API_BASE}/certificates/stats`);
    if (params?.tenantSlug) url.searchParams.set('tenantSlug', params.tenantSlug);
    if (params?.search) url.searchParams.set('search', params.search);
    if (params?.from) url.searchParams.set('from', params.from);
    if (params?.to) url.searchParams.set('to', params.to);
    if (typeof params?.limit === 'number' && Number.isFinite(params.limit)) {
        url.searchParams.set('limit', String(params.limit));
    }

    const res = await fetchWithAuth(url.toString(), { cache: 'no-store' });
    if (!res.ok) {
        throw await parseApiError(res, 'Failed to load certificate stats');
    }
    return res.json();
}

export function trackCertificateVerificationView(params: {
    contract: string;
    tokenId: number;
    networkId: number;
    tenantSlug?: string;
}) {
    try {
        const url = new URL(
            `${process.env.NEXT_PUBLIC_API_BASE}/certificates/verify/${params.contract}/${params.tokenId}/${params.networkId}/view`,
        );
        if (params.tenantSlug) {
            url.searchParams.set('tenantSlug', params.tenantSlug);
        }

        const payload = JSON.stringify({});

        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
            const blob = new Blob([payload], { type: 'application/json' });
            navigator.sendBeacon(url.toString(), blob);
            return;
        }

        void fetch(url.toString(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
        }).catch(() => undefined);
    } catch {
        // best-effort only
    }
}

export async function resolveShortCode(shortCode: string): Promise<{
    authorized: boolean;
    shortCode: string;
    originalUrl?: string;
    createdAt?: string;
    signature?: string | null;
}> {
    const res = await fetch(
        `${API_BASE}/shortener/resolve/${encodeURIComponent(shortCode)}`,
    );
    if (!res.ok) throw new Error('Short URL not found');
    return res.json();
}

export const updateOnboardingStatus = async (completed: boolean) => {
    // This used to return the raw Response without checking it, so callers read
    // any resolution as success: the profile page announced "Tutorial reset!"
    // on a 403, and the guided tour never recorded completion so it replayed.
    const res = await fetchWithAuth(`${API_BASE}/users/me/onboarding`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ completed }),
    });
    if (!res.ok) throw await parseApiError(res, 'Failed to update onboarding status');
    return res;
};
